import { useEffect, useRef, useState } from 'react';
import { searchClient, GlobalSearchData } from '../client/searchClient';

export const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 250;
const RECENTS_KEY = 'hr.globalSearch.recents';
const RECENTS_MAX = 5;

const readRecents = (): string[] => {
    try {
        const raw = localStorage.getItem(RECENTS_KEY);
        const parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed.filter((x) => typeof x === 'string') : [];
    } catch {
        return [];
    }
};

/**
 * Debounced org-scoped global search for the top-bar field. Results only ever
 * come from the server — below the minimum length there is no data, no
 * spinner, no stale leftovers. Aborts the in-flight request on every
 * keystroke so a slow early response can never overwrite a newer one.
 * Also keeps the short list of recent queries (localStorage) shown before
 * the user has typed enough to search.
 */
export const useGlobalSearch = () => {
    const [query, setQuery] = useState('');
    const [data, setData] = useState<GlobalSearchData | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [recents, setRecents] = useState<string[]>(readRecents);
    const abortRef = useRef<AbortController | null>(null);

    const trimmed = query.trim();

    useEffect(() => {
        abortRef.current?.abort();
        if (trimmed.length < MIN_QUERY_LENGTH) {
            setData(null);
            setLoading(false);
            setError(false);
            return;
        }

        const controller = new AbortController();
        abortRef.current = controller;
        setLoading(true);
        const timer = setTimeout(async () => {
            try {
                const result = await searchClient.search(trimmed, controller.signal);
                if (controller.signal.aborted) return;
                setData(result);
                setError(false);
                setLoading(false);
            } catch {
                if (controller.signal.aborted) return;
                setData(null);
                setError(true);
                setLoading(false);
            }
        }, DEBOUNCE_MS);

        return () => {
            clearTimeout(timer);
            controller.abort();
        };
    }, [trimmed]);

    /** Called when a result is actually opened — that query earned a recall slot. */
    const rememberQuery = (value: string) => {
        const next = [value, ...recents.filter((r) => r.toLowerCase() !== value.toLowerCase())].slice(0, RECENTS_MAX);
        setRecents(next);
        try {
            localStorage.setItem(RECENTS_KEY, JSON.stringify(next));
        } catch {
            // Storage unavailable (private mode) — recents just stay in-memory.
        }
    };

    return { query, setQuery, trimmed, data, loading, error, recents, rememberQuery };
};
